import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Star, CheckCircle2, Store } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { applyStoreLanguage } from "@/i18n";

export default function ReviewPage() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderId = params.get("commande") || "";
  const [info, setInfo] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    api.get(`/public/store/${slug}/review-status?commande=${encodeURIComponent(orderId)}`)
      .then((r) => { if (active) { setInfo(r.data); applyStoreLanguage(r.data?.business_language); } })
      .catch(() => { if (active) setInfo({ valid_order: false }); });
    return () => { active = false; };
  }, [slug, orderId]);

  const submit = async () => {
    if (!rating) { toast.error(t("review.selectRating")); return; }
    setBusy(true);
    try {
      await api.post(`/public/store/${slug}/reviews`, { order_id: orderId, rating, comment });
      setDone(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0] flex flex-col items-center px-5 pt-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="h-14 w-14 rounded-2xl bg-[#1B4332] flex items-center justify-center mx-auto mb-3">
            <Store className="h-7 w-7 text-white" />
          </div>
          <h1 className="font-display text-2xl font-extrabold">{info?.business_name || t("review.yourReview")}</h1>
        </div>

        {done || info?.already_reviewed ? (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl p-8 text-center shadow-sm" data-testid="review-thanks">
            <CheckCircle2 className="h-14 w-14 text-[#2D6A4F] mx-auto mb-3" />
            <h2 className="font-display text-xl font-extrabold mb-1">{t("review.thanksTitle")}</h2>
            <p className="text-sm text-[#5C574F] mb-6">{t("review.thanksDesc")}</p>
            <button onClick={() => navigate(`/boutique/${slug}`)} data-testid="goto-store-btn" className="w-full h-13 py-3.5 rounded-2xl bg-[#E85D04] text-white font-bold active:scale-95 transition-transform">
              {t("review.seeStore")}
            </button>
          </motion.div>
        ) : info && !info.valid_order ? (
          <div className="bg-white rounded-3xl p-8 text-center shadow-sm" data-testid="review-invalid">
            <p className="text-[#5C574F]">{t("review.invalidLink")}</p>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl p-6 shadow-sm">
            <p className="text-center text-sm text-[#5C574F] mb-4">{t("review.howWasOrder", { name: info?.customer_name ? `, ${info.customer_name}` : "" })}</p>
            <div className="flex justify-center gap-2 mb-5" data-testid="star-picker">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`star-${n}`}
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  className="active:scale-90 transition-transform"
                  aria-label={`${n} étoiles`}
                >
                  <Star className={`h-10 w-10 ${(hover || rating) >= n ? "fill-[#FFBA08] text-[#FFBA08]" : "text-[#E5E0D8]"}`} />
                </button>
              ))}
            </div>
            <textarea
              data-testid="review-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              maxLength={500}
              placeholder={t("review.shareExp")}
              className="w-full bg-[#F9F6F0] rounded-2xl p-4 outline-none border border-transparent focus:border-[#E85D04] resize-none mb-4 text-sm"
            />
            <button
              data-testid="submit-review-btn"
              onClick={submit}
              disabled={busy}
              className="w-full h-14 rounded-2xl bg-[#E85D04] text-white font-bold active:scale-95 transition-transform disabled:opacity-60"
            >
              {busy ? t("common.sending") : t("review.send")}
            </button>
          </motion.div>
        )}
        <p className="text-center text-xs text-[#9c968a] mt-6">{t("review.poweredBy")}</p>
      </div>
    </div>
  );
}
